<template>
  <div v-if="!isConfirmed">
    <div class="form-group">
      <label class="form-label">Group</label>
      <template v-if="targetTutor">
        <ValidationProvider rules="required" ref="group" v-slot="{ errors }">
          <SelectGroupIdInput :key="targetTutor.id"
            :tutor="targetTutor" v-model="selectedGroupId" />
          <div class="text-danger" v-for="(error, i) of errors" :key="i">{{ error }}</div>
        </ValidationProvider>
      </template>
      <template v-else>
        <div class="text-danger">
          Please select Tutor first.
        </div>
      </template>
    </div>

    <div v-if="group">
      <div class="form-group">
        <label class="form-label">Students</label>
        <p class="form-confirm-value" v-for="student of group.students" :key="student.id">
          {{ student.name }}
        </p>
      </div>
      <div class="form-group">
        <label class="form-label">University</label>
        <div class="form-confirm-value">{{ group.university.name }}</div>
      </div>
      <div class="form-group">
        <label class="form-label">Campus</label>
        <div class="form-confirm-value">{{ group.campus.name }}</div>
      </div>
      <div class="form-group">
        <label class="form-label">Day of week</label>
        <div class="form-confirm-value">{{ group.dayOfWeek | weekday }}</div>
      </div>
      <div class="form-group">
        <label class="form-label">Period</label>
        <div class="form-confirm-value">{{ group.period }}</div>
      </div>

      <div class="classRoom">
        <div class="form-group">
          <label class="form-label">Booth From</label>
          <div class="form-confirm-value" v-if="group.booth">
            {{ group.booth.building }} - {{ group.booth.room }} - {{ group.booth.number }}
          </div>
        </div>
        <div class="form-group">
          <div class="form-label">Booth To</div>
          <ValidationProvider rules="required" ref="booth" v-slot="{ errors }">
            <SelectBoothInput v-model="toBooth"
              :university="group.university" :campus="group.campus" />
            <div class="text-danger" v-for="(error, i) of errors" :key="i">{{ error }}</div>
          </ValidationProvider>
        </div>
      </div>

      <div class="form-group">
        <div class="form-label">Comment</div>
        <ValidationProvider ref="comment" v-slot="{ errors }">
          <Textarea class="form-control" v-model="comment" />
          <div class="text-danger" v-for="(error, i) of errors" :key="i">{{ error }}</div>
        </ValidationProvider>
      </div>
    </div>
  </div>

  <div v-else>
    <div class="form-group">
      <label class="form-label">Group</label>
      <div class="form-confirm-value">
        {{ group.campus.name }}
        {{ group.fiscalYear }}
        {{ group.semester.name }}
        {{ group.dayOfWeek | weekday }}
        Period{{ group.period }}
      </div>
    </div>

    <div class="form-group">
      <label class="form-label">Students</label>
      <p class="form-confirm-value" v-for="student of group.students" :key="student.id">
        {{ student.name }}
      </p>
    </div>

    <div class="form-group">
      <label class="form-label">University</label>
      <div class="form-confirm-value">{{ group.university.name }}</div>
    </div>

    <div class="form-group">
      <label class="form-label">Campus</label>
      <div class="form-confirm-value">{{ group.campus.name }}</div>
    </div>

    <div class="form-group">
      <label class="form-label">Day of week</label>
      <div class="form-confirm-value">{{ group.dayOfWeek | weekday }}</div>
    </div>

    <div class="form-group">
      <label class="form-label">Period</label>
      <div class="form-confirm-value">{{ group.period }}</div>
    </div>

    <div class="form-group">
      <label class="form-label">Booth From</label>
      <div class="form-confirm-value">
        {{ group.booth.building }} - {{ group.booth.room }} - {{ group.booth.number }}
      </div>
    </div>

    <div class="form-group">
      <div class="form-label">Booth To</div>
      <div class="form-confirm-value">
        {{ toBooth.building }} - {{ toBooth.room }} - {{ toBooth.number }}
      </div>
    </div>

    <div class="form-group">
      <div class="form-label">Comment</div>
      <div class="form-confirm-value">{{ comment }}</div>
    </div>
    <div class="form-group" v-if="isExecuted()">
      <label>{{ status | status }} Comment</label>
      <div class="form-confirm-value">{{ approvedComment }}</div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  Component,
  Prop,
  Vue,
  Watch,
} from 'vue-property-decorator'
import _ from 'lodash'
import { ValidationProvider } from 'vee-validate'
import { Tutor } from '@/store/tutors/types'
import { Group, GroupConditions } from '@/store/groups/types'
import { RootState } from '@/store/types'
import { weekdayKey, Weekdays } from '@/store/common.types'
import { IAddApplicationComponent } from '@/components/pages/application/Add.vue'
import SelectGroupIdInput from '@/components/parts/inputItem/SelectGroupIdInput.vue'
import SelectBoothInput from '@/components/parts/inputItem/SelectBoothInput.vue'
import Textarea from '@/components/parts/inputItem/Textarea.vue'
import { Booth } from '@/store/booths/types'
import { BoothApplication } from '@/store/applications/booths/types'
import { applicationStatusCode, ApplicationStatus } from '@/store/applications/types'

@Component({
  components: {
    ValidationProvider,
    SelectGroupIdInput,
    SelectBoothInput,
    Textarea,
  },
  filters: {
    weekday(code: weekdayKey): string {
      return Weekdays[code]
    },
    status: (s: applicationStatusCode): string => ApplicationStatus[s],
  },
})
export default class ApplicationAddBooth extends Vue implements IAddApplicationComponent {
  @Prop({ required: true }) isConfirmed!: boolean

  // 編集時・詳細時など、初期値が必要な場合
  @Prop() initialValues!: BoothApplication

  @Prop() targetTutor!: Tutor

  @Prop() status!: applicationStatusCode

  private isValidated: boolean = false

  private selectedGroupId: string|null = null

  private toBooth: Booth|null = null

  private comment: string|null = null

  private approvedComment: string|null = null

  @Watch('targetTutor')
  private chengedTutor() {
    const conditions: GroupConditions = {
      tutor: this.targetTutor,
      university: null,
      campuses: [],
      weekdays: [],
      periods: [],
      fiscalYearSemester: null,
    }
    this.$store.dispatch('fetchGroups', { conditions, page: 1 })
  }

  @Watch('selectedGroupId')
  private changedGroup() {
    this.$store.dispatch('getGroupById', { groupId: this.selectedGroupId })
  }

  private created() {
    if (this.initialValues) {
      this.selectedGroupId = this.initialValues.group.id
      this.toBooth = this.initialValues.toBooth
      this.comment = this.initialValues.comment
      this.approvedComment = this.initialValues?.approvedComment || null
    }

    if (this.targetTutor) {
      this.chengedTutor()
    }
  }

  private get joinedGroups(): Group[] {
    return (this.$store.state as RootState).groups.groups
  }

  private get group(): Group|null {
    if (!this.selectedGroupId) {
      return null
    }
    return this.$store.getters.findGroupById(this.selectedGroupId)
  }

  /**
   * バリデーション（ApplicationAdd.vueから呼び出し）
   * @return {Promise} バリデーション結果
   */
  public validation(): Promise<boolean> {
    return Promise.all([
      'group', 'booth', 'comment',
    ].map((key) => (this.$refs[key] as any).validate()))
      .then((validationResults) => {
        const errorLength = _.filter(validationResults, { valid: false }).length

        if (errorLength === 0) {
          this.isValidated = true
          return true
        }
        return false
      })
  }

  /**
   * 登録（ApplicationAdd.vueから呼び出し）
   * @return {Promise} ActionからのPromise
   */
  public register(tutor: Tutor): Promise<void> {
    const application: BoothApplication = {
      type: 'booth',
      group: this.group as Group,
      toBooth: this.toBooth as Booth,
      comment: this.comment,
    }

    return this.$store.dispatch('registerBoothApplication', application)
  }

  /**
   * 申請のステータスが未承認以外かチェック
   */
  private isExecuted() {
    return this.status !== 'unapproved'
  }
}
</script>

<style>

</style>
